import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PortalNav } from "@/components/PortalNav";
import { Database, Plug, Loader2, CheckCircle2, AlertTriangle, Table2, RefreshCw, Users, GitMerge, UserPlus } from "lucide-react";

const COMPARE_COLS = [
  { key: "Name", label: "Customer" },
  { key: "AccountNumber", label: "Account #" },
  { key: "Status", label: "Status" },
  { key: "Active", label: "Active" },
  { key: "SalesPerson", label: "Salesperson" },
  { key: "City", label: "City" },
  { key: "State", label: "State" },
  { key: "Balance", label: "Balance" },
  { key: "Terms", label: "Terms" },
  { key: "Email", label: "Email" },
];

export default function ReportsAdmin() {
  const [testing, setTesting] = useState(false);
  const [conn, setConn] = useState(null);
  const [tables, setTables] = useState([]);
  const [tablesErr, setTablesErr] = useState("");
  const [loadingTables, setLoadingTables] = useState(false);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [limit, setLimit] = useState(50);
  const [query, setQuery] = useState("");
  const [previewQuery, setPreviewQuery] = useState("");
  const [sort, setSort] = useState({ col: null, dir: 1 });
  const [db, setDb] = useState("peregrine");
  const [tab, setTab] = useState("tables");
  const [cmp, setCmp] = useState(null);
  const [cmpErr, setCmpErr] = useState("");
  const [loadingCmp, setLoadingCmp] = useState(false);
  const [cmpQuery, setCmpQuery] = useState("");
  const [mrg, setMrg] = useState(null);
  const [mrgErr, setMrgErr] = useState("");
  const [loadingMrg, setLoadingMrg] = useState(false);
  const [selProspect, setSelProspect] = useState(null);
  const [selErps, setSelErps] = useState([]);
  const [mrgProspectQ, setMrgProspectQ] = useState("");
  const [mrgErpQ, setMrgErpQ] = useState("");
  const [merging, setMerging] = useState(false);
  const [adding, setAdding] = useState(false);

  const testConnection = async () => {
    setTesting(true);
    setConn(null);
    try {
      const { data } = await api.get("/reports/sql-test", { params: { database: db || undefined } });
      setConn(data);
      if (data.connected) toast.success("Connected to SQL server");
      else toast.error("Connection blocked — see details");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const loadTables = async () => {
    setLoadingTables(true);
    setTablesErr("");
    try {
      const { data } = await api.get("/reports/sql-tables", { params: { database: db || undefined } });
      if (data.connected) setTables(data.tables || []);
      else { setTables([]); setTablesErr(data.error || "Could not read tables"); }
    } catch (e) {
      setTablesErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not read tables");
    } finally {
      setLoadingTables(false);
    }
  };

  const openTable = async (t, lim = limit) => {
    setSelected(t);
    setLoadingPreview(true);
    setPreview(null);
    setPreviewErr("");
    setPreviewQuery("");
    setSort({ col: null, dir: 1 });
    try {
      const { data } = await api.get("/reports/sql-preview", { params: { schema: t.schema, table: t.table, limit: lim, database: db || undefined } });
      if (data.connected) setPreview(data);
      else setPreviewErr(data.error || "Could not read rows");
    } catch (e) {
      setPreviewErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not read rows");
    } finally {
      setLoadingPreview(false);
    }
  };

  const loadCompare = async () => {
    setLoadingCmp(true);
    setCmpErr("");
    setCmp(null);
    try {
      const { data } = await api.get("/reports/compare-customers", { params: { database: db || undefined } });
      if (data.connected) setCmp(data);
      else setCmpErr(data.error || "Could not run comparison");
    } catch (e) {
      setCmpErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not run comparison");
    } finally {
      setLoadingCmp(false);
    }
  };

  const loadMerge = async () => {
    setLoadingMrg(true);
    setMrgErr("");
    setMrg(null);
    setSelProspect(null);
    setSelErps([]);
    try {
      const { data } = await api.get("/reports/merge-data", { params: { database: db || undefined } });
      if (data.connected) setMrg(data);
      else setMrgErr(data.error || "Could not load merge data");
    } catch (e) {
      setMrgErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not load merge data");
    } finally {
      setLoadingMrg(false);
    }
  };

  const mergeNow = async () => {
    if (!selProspect || selErps.length !== 1) return;
    const erp = selErps[0];
    setMerging(true);
    try {
      await api.post("/reports/merge-customer", { crm_id: selProspect.id, name: erp.Name, account_number: erp.AccountNumber || "" });
      toast.success(`Merged "${selProspect.company_name}" → "${erp.Name}"`);
      loadMerge();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  const addNow = async () => {
    if (selErps.length === 0) return;
    setAdding(true);
    try {
      const payload = { customers: selErps.map((e) => ({ name: e.Name, account_number: e.AccountNumber || "", city: e.City || "", state: e.State || "", phone: e.Phone || "", email: e.Email || "", sales_person: e.SalesPerson || "" })) };
      const { data } = await api.post("/reports/add-customers", payload);
      toast.success(`Added ${data.created} customer(s)${data.skipped?.length ? `, skipped ${data.skipped.length}` : ""}`);
      loadMerge();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Add failed");
    } finally {
      setAdding(false);
    }
  };

  useEffect(() => { testConnection(); loadTables(); /* eslint-disable-next-line */ }, []);

  const filtered = tables.filter((t) => `${t.schema}.${t.table}`.toLowerCase().includes(query.toLowerCase()));

  let cmpRows = cmp ? (cmp.erp_not_in_crm || []) : [];
  if (cmp && cmpQuery.trim()) {
    const q = cmpQuery.toLowerCase();
    cmpRows = cmpRows.filter((r) => COMPARE_COLS.some((c) => r[c.key] != null && String(r[c.key]).toLowerCase().includes(q)));
  }

  let mrgProspects = mrg ? (mrg.prospects || []) : [];
  if (mrg && mrgProspectQ.trim()) {
    const q = mrgProspectQ.toLowerCase();
    mrgProspects = mrgProspects.filter((p) => (p.company_name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q));
  }
  let mrgErp = mrg ? (mrg.erp_unmatched || []) : [];
  if (mrg && mrgErpQ.trim()) {
    const q = mrgErpQ.toLowerCase();
    mrgErp = mrgErp.filter((e) => (e.Name || "").toLowerCase().includes(q) || (e.AccountNumber || "").toLowerCase().includes(q));
  }

  let displayRows = preview ? preview.rows : [];
  if (preview && previewQuery.trim()) {
    const q = previewQuery.toLowerCase();
    displayRows = displayRows.filter((r) => r.some((v) => v !== null && String(v).toLowerCase().includes(q)));
  }
  if (preview && sort.col !== null) {
    const idx = sort.col;
    displayRows = [...displayRows].sort((a, b) => {
      const av = a[idx], bv = b[idx];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      const na = Number(av), nb = Number(bv);
      const bothNum = av !== "" && bv !== "" && !isNaN(na) && !isNaN(nb);
      if (bothNum) return (na - nb) * sort.dir;
      return String(av).localeCompare(String(bv)) * sort.dir;
    });
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5] md:pl-[var(--sb-w,16rem)]">
      <PortalNav />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-1"><Database className="h-5 w-5 text-[#D4AF37]" /><span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Admin</span></div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight text-[#0F172A] mb-6">SQL Reports</h1>

        {/* Connection card */}
        <div className="bg-white border border-zinc-200 p-5 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-display font-bold text-[#0F172A] flex items-center gap-2"><Plug className="h-4 w-4 text-[#D4AF37]" /> External SQL connection</div>
              <div className="text-xs text-zinc-500 font-mono mt-1">fsreports.database.windows.net</div>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Database</label>
                <input value={db} onChange={(e) => setDb(e.target.value)} data-testid="reports-db-input" placeholder="peregrine"
                  className="px-3 py-2 border border-zinc-300 text-sm w-40 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
              </div>
              <button onClick={testConnection} disabled={testing} data-testid="reports-test-conn-btn"
                className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60">
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Test Connection
              </button>
            </div>
          </div>
          {conn && (
            <div data-testid="reports-conn-status" className={`mt-4 p-4 border text-sm ${conn.connected ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
              {conn.connected ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Connected</div>
                  <div className="text-xs text-zinc-600 font-mono break-all">{conn.version}</div>
                  <div className="text-xs text-zinc-500">Database: <b>{conn.database}</b> · Egress IP: <b>{conn.egress_ip || "?"}</b></div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 font-semibold text-red-700"><AlertTriangle className="h-4 w-4" /> Not connected</div>
                  <div className="text-xs text-zinc-500">Egress IP: <b>{conn.egress_ip || "?"}</b> — this IP must be allowlisted in the Azure SQL firewall.</div>
                  <div className="text-xs text-red-800 font-mono whitespace-pre-wrap break-all mt-1">{conn.error}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-zinc-200 mb-6">
          <button onClick={() => setTab("tables")} data-testid="reports-tab-tables"
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "tables" ? "border-[#D4AF37] text-[#0F172A]" : "border-transparent text-zinc-500 hover:text-[#0F172A]"}`}>
            <span className="inline-flex items-center gap-2"><Table2 className="h-4 w-4" /> SQL Tables</span>
          </button>
          <button onClick={() => { setTab("compare"); if (!cmp && !loadingCmp) loadCompare(); }} data-testid="reports-tab-compare"
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "compare" ? "border-[#D4AF37] text-[#0F172A]" : "border-transparent text-zinc-500 hover:text-[#0F172A]"}`}>
            <span className="inline-flex items-center gap-2"><Users className="h-4 w-4" /> Compare Customers</span>
          </button>
          <button onClick={() => { setTab("merge"); if (!mrg && !loadingMrg) loadMerge(); }} data-testid="reports-tab-merge"
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "merge" ? "border-[#D4AF37] text-[#0F172A]" : "border-transparent text-zinc-500 hover:text-[#0F172A]"}`}>
            <span className="inline-flex items-center gap-2"><GitMerge className="h-4 w-4" /> Merge Customer</span>
          </button>
        </div>

        {tab === "tables" && (
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
          <div className="bg-white border border-zinc-200">
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
              <div className="flex items-center gap-2"><Table2 className="h-4 w-4 text-[#D4AF37]" /><h2 className="font-display font-bold text-sm text-[#0F172A]">Tables</h2><span className="text-xs text-zinc-400">({tables.length})</span></div>
              <button onClick={loadTables} disabled={loadingTables} data-testid="reports-reload-tables" className="text-zinc-400 hover:text-[#0F172A]"><RefreshCw className={`h-4 w-4 ${loadingTables ? "animate-spin" : ""}`} /></button>
            </div>
            <div className="p-2">
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter tables…" data-testid="reports-table-filter"
                className="w-full px-3 py-2 border border-zinc-200 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] mb-2" />
            </div>
            <div className="max-h-[520px] overflow-auto" data-testid="reports-tables-list">
              {loadingTables ? (
                <div className="p-8 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              ) : tablesErr ? (
                <div className="p-4 text-xs text-red-700 whitespace-pre-wrap break-all">{tablesErr}</div>
              ) : filtered.length === 0 ? (
                <div className="p-8 text-center text-zinc-400 text-sm">No tables.</div>
              ) : filtered.map((t) => {
                const key = `${t.schema}.${t.table}`;
                const active = selected && selected.schema === t.schema && selected.table === t.table;
                return (
                  <button key={key} onClick={() => openTable(t)} data-testid={`reports-table-item-${key}`}
                    className={`w-full text-left px-4 py-2 text-sm border-l-2 transition-colors ${active ? "bg-[#0F172A]/5 border-[#D4AF37] text-[#0F172A] font-medium" : "border-transparent text-zinc-600 hover:bg-zinc-50"}`}>
                    <span className="text-zinc-400 text-xs">{t.schema}.</span>{t.table}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-white border border-zinc-200 min-h-[300px]">
            <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-display font-bold text-[#0F172A] text-sm truncate">{selected ? `${selected.schema}.${selected.table}` : "Select a table"}</h2>
              {selected && (
                <div className="flex items-center gap-2">
                  {preview && preview.rows.length > 0 && (
                    <input value={previewQuery} onChange={(e) => setPreviewQuery(e.target.value)} placeholder="Filter rows…" data-testid="reports-preview-filter"
                      className="text-xs border border-zinc-300 px-2 py-1 outline-none focus:ring-2 focus:ring-[#D4AF37] w-48" />
                  )}
                  <select value={limit} onChange={(e) => { const l = Number(e.target.value); setLimit(l); openTable(selected, l); }} data-testid="reports-limit"
                    className="text-xs border border-zinc-300 bg-white px-2 py-1 outline-none focus:ring-2 focus:ring-[#D4AF37]">
                    {[50, 100, 200].map((n) => <option key={n} value={n}>Top {n} rows</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="overflow-auto max-h-[560px]" data-testid="reports-preview-panel">
              {loadingPreview ? (
                <div className="p-12 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              ) : previewErr ? (
                <div className="p-4 text-xs text-red-700 whitespace-pre-wrap break-all">{previewErr}</div>
              ) : !preview ? (
                <div className="p-12 text-center text-zinc-400 text-sm">Pick a table on the left to preview its rows.</div>
              ) : preview.rows.length === 0 ? (
                <div className="p-12 text-center text-zinc-400 text-sm">Table is empty.</div>
              ) : (
                <table className="w-full text-xs" data-testid="reports-preview-table">
                  <thead className="sticky top-0 bg-zinc-100">
                    <tr>{preview.columns.map((c, ci) => (
                      <th key={c} onClick={() => setSort((s) => ({ col: ci, dir: s.col === ci ? -s.dir : 1 }))}
                        className="text-left font-semibold text-zinc-600 px-3 py-2 whitespace-nowrap border-b border-zinc-200 cursor-pointer hover:bg-zinc-200 select-none">
                        {c}{sort.col === ci ? (sort.dir === 1 ? " ▲" : " ▼") : ""}
                      </th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {displayRows.map((row, i) => (
                      <tr key={i} className="odd:bg-white even:bg-zinc-50">
                        {row.map((v, j) => <td key={j} className="px-3 py-1.5 whitespace-nowrap text-zinc-700 max-w-[280px] truncate" title={v === null ? "" : String(v)}>{v === null ? <span className="text-zinc-300">null</span> : String(v)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {preview && preview.rows.length > 0 && <div className="px-5 py-2 border-t border-zinc-100 text-xs text-zinc-400" data-testid="reports-row-count">{displayRows.length} of {preview.rows.length} row(s){previewQuery ? " (filtered)" : ""} · fetched up to {preview.limit}</div>}
          </div>
        </div>
        )}

        {tab === "compare" && (
          <div className="bg-white border border-zinc-200" data-testid="reports-compare-panel">
            <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-display font-bold text-[#0F172A] flex items-center gap-2"><Users className="h-4 w-4 text-[#D4AF37]" /> ERP customers not in CRM</div>
                <div className="text-xs text-zinc-500 mt-0.5">Matched on company name, account # and email against Peregrine.CustomerInformation.</div>
              </div>
              <div className="flex items-center gap-2">
                {cmp && cmp.connected && (
                  <input value={cmpQuery} onChange={(e) => setCmpQuery(e.target.value)} placeholder="Filter…" data-testid="reports-compare-filter"
                    className="text-xs border border-zinc-300 px-2 py-1.5 outline-none focus:ring-2 focus:ring-[#D4AF37] w-48" />
                )}
                <button onClick={loadCompare} disabled={loadingCmp} data-testid="reports-compare-refresh"
                  className="flex items-center gap-2 bg-[#0F172A] text-white px-3 py-1.5 text-xs font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60">
                  {loadingCmp ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
                </button>
              </div>
            </div>

            {cmp && cmp.connected && (
              <div className="grid grid-cols-3 gap-px bg-zinc-200 border-b border-zinc-200" data-testid="reports-compare-summary">
                <div className="bg-white px-5 py-3"><div className="text-2xl font-display font-extrabold text-[#0F172A]">{cmp.summary.erp_total}</div><div className="text-xs text-zinc-500">ERP customers</div></div>
                <div className="bg-white px-5 py-3"><div className="text-2xl font-display font-extrabold text-emerald-600">{cmp.summary.matched_in_crm}</div><div className="text-xs text-zinc-500">Already in CRM</div></div>
                <div className="bg-white px-5 py-3"><div className="text-2xl font-display font-extrabold text-[#D4AF37]">{cmp.summary.erp_not_in_crm}</div><div className="text-xs text-zinc-500">Not in CRM</div></div>
              </div>
            )}

            <div className="overflow-auto max-h-[560px]">
              {loadingCmp ? (
                <div className="p-12 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              ) : cmpErr ? (
                <div className="p-4 text-xs text-red-700 whitespace-pre-wrap break-all">{cmpErr}</div>
              ) : !cmp ? (
                <div className="p-12 text-center text-zinc-400 text-sm">Click Refresh to run the comparison.</div>
              ) : cmpRows.length === 0 ? (
                <div className="p-12 text-center text-zinc-400 text-sm">Every ERP customer is already in your CRM{cmpQuery ? " (for this filter)" : ""}.</div>
              ) : (
                <table className="w-full text-xs" data-testid="reports-compare-table">
                  <thead className="sticky top-0 bg-zinc-100">
                    <tr>{COMPARE_COLS.map((c) => <th key={c.key} className="text-left font-semibold text-zinc-600 px-3 py-2 whitespace-nowrap border-b border-zinc-200">{c.label}</th>)}</tr>
                  </thead>
                  <tbody>
                    {cmpRows.map((r, i) => (
                      <tr key={i} className="odd:bg-white even:bg-zinc-50" data-testid={`reports-compare-row-${i}`}>
                        {COMPARE_COLS.map((c) => <td key={c.key} className="px-3 py-1.5 whitespace-nowrap text-zinc-700 max-w-[260px] truncate" title={r[c.key] == null ? "" : String(r[c.key])}>{r[c.key] == null || r[c.key] === "" ? <span className="text-zinc-300">—</span> : String(r[c.key])}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {cmp && cmp.connected && cmpRows.length > 0 && <div className="px-5 py-2 border-t border-zinc-100 text-xs text-zinc-400" data-testid="reports-compare-count">{cmpRows.length} of {cmp.erp_not_in_crm.length} not in CRM{cmpQuery ? " (filtered)" : ""}</div>}
          </div>
        )}

        {tab === "merge" && (
          <div data-testid="reports-merge-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div>
                <div className="font-display font-bold text-[#0F172A] flex items-center gap-2"><GitMerge className="h-4 w-4 text-[#D4AF37]" /> Merge a Prospect with an ERP customer</div>
                <div className="text-xs text-zinc-500 mt-0.5">Pick one CRM Prospect and one unmatched ERP customer. Merging copies the ERP <b>name</b> and <b>account #</b> onto the Prospect (nothing else changes).</div>
              </div>
              <button onClick={loadMerge} disabled={loadingMrg} data-testid="reports-merge-refresh"
                className="flex items-center gap-2 bg-[#0F172A] text-white px-3 py-1.5 text-xs font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60">
                {loadingMrg ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
              </button>
            </div>

            {loadingMrg ? (
              <div className="p-12 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
            ) : mrgErr ? (
              <div className="p-4 bg-white border border-zinc-200 text-xs text-red-700 whitespace-pre-wrap break-all">{mrgErr}</div>
            ) : !mrg ? (
              <div className="p-12 text-center text-zinc-400 text-sm bg-white border border-zinc-200">Click Refresh to load prospects and ERP customers.</div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* CRM Prospects */}
                  <div className="bg-white border border-zinc-200">
                    <div className="px-4 py-3 border-b border-zinc-200">
                      <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#2563EB]" /><h2 className="font-display font-bold text-sm text-[#0F172A]">CRM Prospects</h2><span className="text-xs text-zinc-400">({mrg.prospects.length})</span></div>
                      <input value={mrgProspectQ} onChange={(e) => setMrgProspectQ(e.target.value)} placeholder="Filter prospects…" data-testid="reports-merge-prospect-filter"
                        className="w-full mt-2 px-3 py-2 border border-zinc-200 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
                    </div>
                    <div className="max-h-[460px] overflow-auto" data-testid="reports-merge-prospect-list">
                      {mrgProspects.length === 0 ? (
                        <div className="p-8 text-center text-zinc-400 text-sm">No prospects{mrgProspectQ ? " match" : ""}.</div>
                      ) : mrgProspects.map((p) => {
                        const active = selProspect && selProspect.id === p.id;
                        return (
                          <button key={p.id} onClick={() => setSelProspect(p)} data-testid={`reports-merge-prospect-${p.id}`}
                            className={`w-full text-left px-4 py-2.5 border-l-2 transition-colors ${active ? "bg-[#2563EB]/5 border-[#2563EB]" : "border-transparent hover:bg-zinc-50"}`}>
                            <div className="text-sm font-medium text-[#2563EB] truncate">{p.company_name || "(no name)"}</div>
                            <div className="text-xs text-zinc-400 truncate">{p.account_number ? `Acct ${p.account_number}` : "no account #"}{p.email ? ` · ${p.email}` : ""}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* ERP unmatched */}
                  <div className="bg-white border border-zinc-200">
                    <div className="px-4 py-3 border-b border-zinc-200">
                      <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#D4AF37]" /><h2 className="font-display font-bold text-sm text-[#0F172A]">ERP customers (not in CRM)</h2><span className="text-xs text-zinc-400">({mrg.erp_unmatched.length}{selErps.length ? ` · ${selErps.length} selected` : ""})</span></div>
                      <input value={mrgErpQ} onChange={(e) => setMrgErpQ(e.target.value)} placeholder="Filter ERP customers…" data-testid="reports-merge-erp-filter"
                        className="w-full mt-2 px-3 py-2 border border-zinc-200 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
                    </div>
                    <div className="max-h-[460px] overflow-auto" data-testid="reports-merge-erp-list">
                      {mrgErp.length === 0 ? (
                        <div className="p-8 text-center text-zinc-400 text-sm">No ERP customers{mrgErpQ ? " match" : ""}.</div>
                      ) : mrgErp.map((e, i) => {
                        const active = selErps.includes(e);
                        return (
                          <button key={`${e.AccountNumber || ""}-${e.Name || ""}-${i}`} onClick={() => setSelErps((prev) => prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e])} data-testid={`reports-merge-erp-${i}`}
                            className={`w-full text-left px-4 py-2.5 border-l-2 transition-colors flex items-start gap-2.5 ${active ? "bg-[#D4AF37]/10 border-[#D4AF37]" : "border-transparent hover:bg-zinc-50"}`}>
                            <span className={`mt-0.5 h-4 w-4 flex-shrink-0 border flex items-center justify-center ${active ? "bg-[#D4AF37] border-[#D4AF37]" : "border-zinc-300 bg-white"}`}>{active && <CheckCircle2 className="h-3 w-3 text-white" />}</span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-medium text-[#0F172A] truncate">{e.Name || "(no name)"}</span>
                              <span className="block text-xs text-zinc-400 truncate">{e.AccountNumber ? `Acct ${e.AccountNumber}` : "no account #"}{(e.City || e.State) ? ` · ${[e.City, e.State].filter(Boolean).join(", ")}` : ""}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="mt-4 bg-white border border-zinc-200 p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="reports-merge-bar">
                  <div className="text-sm">
                    {selErps.length >= 2 ? (
                      <span className="text-zinc-700"><b>{selErps.length}</b> ERP customers selected — use <b>Add</b> to create them in CRM. (Merge needs exactly one.)</span>
                    ) : selErps.length === 1 && selProspect ? (
                      <span className="text-zinc-700"><b>Merge</b> into <b className="text-[#2563EB]">{selProspect.company_name}</b> → <b className="text-[#0F172A]">{selErps[0].Name}</b>, or <b>Add</b> as a new customer.</span>
                    ) : selErps.length === 1 ? (
                      <span className="text-zinc-700"><b>Add</b> “{selErps[0].Name}” as a new customer, or pick a Prospect to <b>Merge</b>.</span>
                    ) : (
                      <span className="text-zinc-400">Select ERP customer(s) to <b>Add</b>, or a Prospect + one ERP to <b>Merge</b>.</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={mergeNow} disabled={!selProspect || selErps.length !== 1 || merging} data-testid="reports-merge-confirm"
                      className="flex items-center gap-2 bg-[#D4AF37] text-[#0F172A] px-5 py-2.5 text-sm font-bold hover:bg-[#c6a233] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                      {merging ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitMerge className="h-4 w-4" />} Merge
                    </button>
                    <button onClick={addNow} disabled={selErps.length === 0 || adding} data-testid="reports-add-confirm"
                      className="flex items-center gap-2 bg-[#0F172A] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#1E293B] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                      {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add{selErps.length > 1 ? ` ${selErps.length}` : ""}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
